/**
 * The two gallery behaviours parcos.com has and Vogue does not.
 *
 * Vogue's `snippets/product-gallery` is rendered unmodified by
 * `sections/parcos-main-product` (see that file's header for why), so it
 * already gives us the thumbnail rail, the per-variant filtering driven by
 * image alt text, the mobile prev/next arrows and a click-to-lightbox. Two
 * things live does are missing from it:
 *
 *   1. chevrons above and below the thumbnail rail, shown only when the rail
 *      holds more than it can display, and
 *   2. a hover zoom on the main image that follows the cursor inside the
 *      image's own box, rather than only opening the lightbox on click.
 *
 * Both are added here rather than by forking the snippet, so the filtering,
 * the carousel and the lightbox stay Vogue's and stay upgradeable. The markup
 * for both is rendered by the section (so the copy and the icons live in
 * Liquid with the rest of the page); this file only relocates it into Vogue's
 * containers and drives it.
 *
 * `<parcos-gallery>` wraps ONLY the product page's gallery. The quick-buy
 * modal renders `product-gallery` too, but with `carousel_dots` and zoom off,
 * so it has neither a rail nor a zoom to extend and is deliberately untouched.
 */
(() => {
  /* Vogue's own desktop breakpoint: below it the rail is display:none and the
     mobile arrows take over, so both behaviours here are desktop-only. */
  const DESKTOP = window.matchMedia('(min-width: 1000px)');
  const FINE_POINTER = window.matchMedia('(pointer: fine)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ParcosGallery extends HTMLElement {
    connectedCallback() {
      // Guard against a second upgrade: `product-rerender` only swaps nodes
      // carrying a `data-block-type` it knows, and this element carries none,
      // so it survives a variant switch -- but a theme-editor section reload
      // can still re-run this.
      if (this.wired) return;

      this.gallery = this.querySelector('product-gallery');
      if (!this.gallery) return;
      this.wired = true;

      this.carousel = this.gallery.querySelector('.product-gallery__carousel');
      this.imageList = this.gallery.querySelector('.product-gallery__image-list');
      this.rail = this.gallery.querySelector('.product-gallery__thumbnail-list');
      this.scroller = this.gallery.querySelector('.product-gallery__thumbnail-scroller');

      this.setUpThumbnailArrows();
      this.setUpZoom();
      this.relabelCarouselArrows();
    }

    /* ------------------------------------------------------------------ *
     * Thumbnail rail
     * ------------------------------------------------------------------ */

    setUpThumbnailArrows() {
      const up = this.querySelector('[data-parcos-thumb-arrow="up"]');
      const down = this.querySelector('[data-parcos-thumb-arrow="down"]');
      if (!up || !down || !this.rail || !this.scroller) return;

      // The buttons are rendered as children of this element and moved into
      // Vogue's rail container. They must NOT land inside
      // `product-gallery-navigation`: that element snapshots every <button>
      // below it at connect time and treats them all as thumbnails, so an
      // arrow in there would shift every thumbnail's index by one.
      this.rail.prepend(up);
      this.rail.append(down);
      this.thumbArrows = { up, down };

      up.addEventListener('click', () => this.scrollThumbnails(-1));
      down.addEventListener('click', () => this.scrollThumbnails(1));

      const sync = () => this.syncThumbnailArrows();
      this.scroller.addEventListener('scroll', sync, { passive: true });
      // `carousel:filter` is what Vogue fires when a variant change re-reads
      // `filtered-indexes`; it is also what hides the thumbnails themselves,
      // so the arrows must be recounted after it, not before.
      this.carousel?.addEventListener('carousel:filter', () => {
        // The hidden attributes are applied by a listener registered before
        // this one, but the reflow they cause is not measurable until the
        // next frame.
        requestAnimationFrame(sync);
      });
      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(this.scroller);
      } else {
        window.addEventListener('resize', sync);
      }
      requestAnimationFrame(sync);
    }

    /** The visible thumbnails: variant filtering hides the rest with [hidden]. */
    get visibleThumbnails() {
      return Array.from(this.gallery.querySelectorAll('.product-gallery__thumbnail')).filter(
        (thumb) => !thumb.hasAttribute('hidden')
      );
    }

    /**
     * One thumbnail per click, as live does. The step is measured from the
     * rail rather than hardcoded so it stays right if the thumbnail size or
     * the gap is re-tuned in parcos.css.
     */
    scrollThumbnails(direction) {
      const thumbs = this.visibleThumbnails;
      if (!thumbs.length) return;
      const step =
        thumbs.length > 1
          ? thumbs[1].offsetTop - thumbs[0].offsetTop
          : thumbs[0].offsetHeight;
      this.scroller.scrollBy({
        top: direction * step,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
    }

    /**
     * Show the arrows only when the rail actually overflows, and disable the
     * one that would do nothing. Reading the overflow rather than counting to
     * five keeps this correct for a variant whose filtered set is smaller than
     * the product's, and for whatever the rail's height is set to.
     */
    syncThumbnailArrows() {
      if (!this.thumbArrows) return;
      const { up, down } = this.thumbArrows;
      const max = this.scroller.scrollHeight - this.scroller.clientHeight;
      // A sub-pixel rail height (zoomed browsers, fractional rem) leaves a
      // pixel of slack that is not a real overflow.
      const overflows = max > 1;
      this.toggleAttribute('data-thumb-overflow', overflows);
      up.disabled = !overflows || this.scroller.scrollTop <= 1;
      down.disabled = !overflows || this.scroller.scrollTop >= max - 1;
    }

    /* ------------------------------------------------------------------ *
     * Hover zoom
     * ------------------------------------------------------------------ */

    setUpZoom() {
      this.zoomPane = this.querySelector('[data-parcos-zoom]');
      if (!this.zoomPane || !this.carousel || !this.imageList) return;

      // The pane is positioned against the image list, not the carousel: the
      // carousel is the horizontally scrolled element, and an absolutely
      // positioned child of a scroll container scrolls away with its content.
      this.imageList.append(this.zoomPane);

      this.carousel.addEventListener('pointermove', (event) => this.onZoomMove(event));
      this.carousel.addEventListener('pointerleave', () => this.hideZoom());
      // A stale zoom of the outgoing image would otherwise sit over the new
      // one until the pointer moves again.
      this.carousel.addEventListener('carousel:change', () => this.hideZoom());
      this.carousel.addEventListener('carousel:filter', () => this.hideZoom());
      window.addEventListener('scroll', () => this.hideZoom(), { passive: true });
    }

    onZoomMove(event) {
      if (event.pointerType !== 'mouse' || !DESKTOP.matches || !FINE_POINTER.matches) return;

      const cell = event.target.closest('.product-gallery__media');
      // Videos and 3D models keep their own controls; only photographs zoom.
      if (!cell || cell.getAttribute('data-media-type') !== 'image') return this.hideZoom();

      const image = cell.querySelector('img');
      if (!image || !image.currentSrc) return this.hideZoom();

      const rect = image.getBoundingClientRect();
      if (!rect.width || !rect.height) return this.hideZoom();

      // Percentage background-position aligns the same relative point of the
      // (oversized) image with that point of the pane, which is what makes the
      // zoom track the cursor. Clamped so a pointer event delivered a fraction
      // outside the box cannot jump the view past the image's edge.
      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
      const position = `${x.toFixed(2)}% ${y.toFixed(2)}%`;

      if (this.zoomedImage === image) {
        this.zoomPane.style.backgroundPosition = position;
        return;
      }

      this.prepareZoom(image, rect);
      this.zoomPane.style.backgroundPosition = position;
      // The pane is still hidden here, so the browser would otherwise treat
      // the first position as the start of a transition from wherever the
      // previous image left it, and the zoom would open mid-sweep.
      void this.zoomPane.offsetWidth;
      this.zoomedImage = image;
      this.zoomPane.classList.add('is-zooming');
    }

    prepareZoom(image, rect) {
      // The pane covers the IMAGE's box rather than its cell, so its aspect
      // ratio always matches the photograph. `background-size` is a single
      // percentage, whose height resolves to `auto` -- together that is what
      // keeps a non-square product shot from being stretched.
      const origin = this.imageList.getBoundingClientRect();
      this.zoomPane.style.insetInlineStart = `${rect.left - origin.left}px`;
      this.zoomPane.style.insetBlockStart = `${rect.top - origin.top}px`;
      this.zoomPane.style.width = `${rect.width}px`;
      this.zoomPane.style.height = `${rect.height}px`;

      // Two layers: the rendition already on screen paints immediately, and
      // the larger one replaces it as soon as it arrives, so the first hover
      // is never blank and never waits on the network.
      const shown = image.currentSrc;
      const detailed = this.detailedSource(shown, rect.width);
      this.zoomPane.style.backgroundImage =
        detailed && detailed !== shown ? `url("${detailed}"), url("${shown}")` : `url("${shown}")`;
    }

    /**
     * Ask Shopify's CDN for the rendition the zoom actually magnifies to —
     * the stage is 500px wide and the pane shows it at 150%, so the picture
     * the browser already has is half the resolution the zoom wants.
     *
     * Anything whose URL this cannot rewrite without changing the crop — a
     * `height` or `crop` term, a placeholder SVG, an image served from
     * somewhere other than Shopify — gives back nothing, and the rendition on
     * screen is magnified instead. A soft zoom beats a distorted one.
     */
    detailedSource(src, displayWidth) {
      try {
        const url = new URL(src, window.location.href);
        const params = url.searchParams;
        if (!params.has('width') || params.has('height') || params.has('crop')) return null;
        const scale =
          parseFloat(getComputedStyle(this.zoomPane).getPropertyValue('--parcos-zoom-scale')) || 150;
        const wanted = Math.round(((displayWidth * scale) / 100) * (window.devicePixelRatio || 1));
        params.set('width', String(Math.min(2048, Math.max(wanted, Math.round(displayWidth)))));
        return url.toString();
      } catch {
        return null;
      }
    }

    hideZoom() {
      if (!this.zoomPane || this.zoomedImage == null) return;
      this.zoomedImage = null;
      this.zoomPane.classList.remove('is-zooming');
    }

    /* ------------------------------------------------------------------ *
     * Mobile arrows
     * ------------------------------------------------------------------ */

    /**
     * Vogue labels its carousel arrows "Previous"/"Next", which says nothing
     * about what they move. The wording is passed in from the section so it
     * sits with the rest of the page's copy.
     */
    relabelCarouselArrows() {
      const label = (selector, text) => {
        const button = this.gallery.querySelector(selector);
        if (button && text) button.setAttribute('aria-label', text);
      };
      label('carousel-prev-button button', this.getAttribute('prev-label'));
      label('carousel-next-button button', this.getAttribute('next-label'));
    }
  }

  if (!window.customElements.get('parcos-gallery')) {
    window.customElements.define('parcos-gallery', ParcosGallery);
  }
})();
